import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSearch } from "@/api/crm";
import { Icon } from "@/components/icons";

const TYPE_LABEL = { contact: "Contact", account: "Account", deal: "Deal" } as const;
const PATH = { contact: "/contacts", account: "/accounts", deal: "/deals" } as const;

/** Header search across contacts, accounts and deals. Ctrl/Cmd+K focuses it. */
export function GlobalSearch() {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const results = useSearch(q);
  const hits = q.length >= 2 ? results.data ?? [] : [];

  useEffect(() => { const t = setTimeout(() => setQ(text.trim()), 200); return () => clearTimeout(t); }, [text]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); input.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (i: number) => {
    const hit = hits[i];
    if (!hit) return;
    navigate(`${PATH[hit.type]}/${hit.id}`);
    setOpen(false); setText(""); input.current?.blur();
  };

  return (
    <div className="relative w-full max-w-md">
      <Icon name="search" className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted" />
      <input ref={input} type="search" role="combobox" aria-expanded={open && hits.length > 0} aria-controls="global-search-results"
        aria-label="Search contacts, accounts and deals" placeholder="Search…" className="input pl-9 md:pr-16"
        value={text} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setText(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === "Enter") { e.preventDefault(); go(active); }
          if (e.key === "Escape") { setOpen(false); input.current?.blur(); }
        }} />
      <kbd className="pointer-events-none absolute right-2 top-2 hidden rounded border border-line bg-canvas px-1.5 py-0.5 text-[11px] text-muted md:block">Ctrl K</kbd>
      {open && q.length >= 2 && (
        <ul id="global-search-results" role="listbox" className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-line bg-surface py-1 shadow-lg">
          {hits.length === 0 && !results.isFetching && <li className="px-3 py-2 text-sm text-muted">No results for “{q}”</li>}
          {hits.map((h, i) => (
            <li key={`${h.type}-${h.id}`} role="option" aria-selected={i === active}
              className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2 ${i === active ? "bg-brand-soft" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); go(i); }} onMouseEnter={() => setActive(i)}>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{h.title}</span>
                {h.subtitle && <span className="block truncate text-xs text-muted">{h.subtitle}</span>}
              </span>
              <span className="shrink-0 rounded bg-canvas px-1.5 py-0.5 text-[11px] font-medium text-muted">{TYPE_LABEL[h.type]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
